import { randomUUID } from "expo-crypto";
import { useCallback, useEffect, useRef } from "react";
import { AppState } from "react-native";

import type {
  PlaybackChunkV1,
  PlaybackTransport,
  PlaybackVideoRendition
} from "@recital-master/shared";

import { recordPlaybackChunks } from "@/firebase/callables";
import { PlaybackChunkAccumulator } from "@/playback/playbackTelemetry";

type UsePlaybackTelemetryOptions = {
  enabled: boolean;
  sessionId: string | null;
  danceId: string | null;
  performanceId: string | null;
  recitalId: string | null;
  entryPositionSeconds: number;
  positionSeconds: number;
  playing: boolean;
  playbackRate: number;
  transport: PlaybackTransport;
  videoRendition: PlaybackVideoRendition | null;
};

export function usePlaybackTelemetry(options: UsePlaybackTelemetryOptions) {
  const recorderRef = useRef<PlaybackChunkAccumulator | null>(null);
  const pendingRef = useRef<PlaybackChunkV1[]>([]);
  const deliveringRef = useRef(false);
  const deliveryScheduledRef = useRef(false);
  const positionRef = useRef(Math.max(0, options.positionSeconds));
  positionRef.current = Math.max(0, options.positionSeconds);

  const drain = useCallback(() => {
    if (deliveringRef.current || pendingRef.current.length === 0) return;
    deliveringRef.current = true;
    const batch = pendingRef.current.splice(0, 20);
    void (async () => {
      let failure: unknown = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          await recordPlaybackChunks({ chunks: batch });
          failure = null;
          break;
        } catch (error) {
          failure = error;
        }
      }
      if (failure) console.warn("[playback-telemetry] delivery failed", failure);
    })().finally(() => {
      deliveringRef.current = false;
      if (pendingRef.current.length > 0) drain();
    });
  }, []);

  const enqueue = useCallback((chunks: Array<PlaybackChunkV1 | null>) => {
    const ready = chunks.filter((chunk): chunk is PlaybackChunkV1 => chunk != null);
    if (ready.length === 0) return;
    pendingRef.current.push(...ready);
    if (deliveryScheduledRef.current) return;
    deliveryScheduledRef.current = true;
    queueMicrotask(() => {
      deliveryScheduledRef.current = false;
      drain();
    });
  }, [drain]);

  const flush = useCallback((positionSeconds = positionRef.current) => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    positionRef.current = Math.max(0, positionSeconds);
    enqueue(recorder.flushAt(positionRef.current, Date.now()));
  }, [enqueue]);

  const identityKey = options.enabled && options.sessionId && options.danceId &&
      options.performanceId && options.recitalId
    ? `${options.sessionId}:${options.danceId}`
    : null;
  useEffect(() => {
    const previous = recorderRef.current;
    enqueue([previous?.flush() ?? null]);
    if (
      !options.enabled ||
      !options.sessionId ||
      !options.danceId ||
      !options.performanceId ||
      !options.recitalId
    ) {
      recorderRef.current = null;
      return;
    }
    recorderRef.current = new PlaybackChunkAccumulator({
      sessionId: options.sessionId,
      playId: randomUUID(),
      danceId: options.danceId,
      performanceId: options.performanceId,
      recitalId: options.recitalId,
      transport: options.transport,
      entryPositionSeconds: options.entryPositionSeconds
    });
    recorderRef.current.setPlaybackRate(options.playbackRate);
    recorderRef.current.setVideoRendition(
      options.videoRendition,
      options.entryPositionSeconds,
      Date.now()
    );
    positionRef.current = Math.max(0, options.entryPositionSeconds);
    enqueue(
      recorderRef.current.setPlaying(
        options.playing,
        positionRef.current,
        Date.now()
      )
    );
    // Identity changes alone create a play; transport and play state have
    // separate effects so neither accidentally creates duplicate playIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, enqueue]);

  useEffect(() => {
    recorderRef.current?.setPlaybackRate(options.playbackRate);
  }, [options.playbackRate]);

  useEffect(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    enqueue(recorder.setTransport(options.transport, positionRef.current, Date.now()));
  }, [enqueue, options.transport]);

  useEffect(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    enqueue(
      recorder.setVideoRendition(
        options.videoRendition,
        positionRef.current,
        Date.now()
      )
    );
  }, [enqueue, options.videoRendition]);

  useEffect(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    enqueue(recorder.setPlaying(options.playing, positionRef.current, Date.now()));
  }, [enqueue, options.playing]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (state) => {
      if (state !== "active") flush();
    });
    return () => subscription.remove();
  }, [flush]);

  useEffect(() => () => flush(), [flush]);

  const recordPosition = useCallback((positionSeconds: number) => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    positionRef.current = Math.max(0, positionSeconds);
    enqueue(recorder.observe(positionRef.current, Date.now()));
  }, [enqueue]);

  return { recordPosition, flush };
}
